import { css } from '@emotion/react';
// Publish is the rows' Download glyph flipped (bar and arrow), so the two actions read as a pair.
import PublishIcon from '@mui/icons-material/Publish';
import { Button, CircularProgress, Link, Skeleton, Tooltip } from '@mui/material';
import { useAtomValue } from 'jotai';
import { ChangeEvent, FC, ReactNode, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { ROUTES } from '../../../constants/RouteNameMapping';
import {
  ALLOWED_EXTENSIONS,
  MAX_BATCH_UPLOAD_SIZE,
  MAX_FILE_SIZE,
} from '../../../constants/file-upload.constants';
import { IFinanceRoundDataModel } from '../../../data-models/finance-round.data-model';
import { IRoundDocumentMetadata } from '../../../data-models/round-document.data-model';
import { PermissionKey } from '../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../services/PermissionService';
import { clientConfigFieldsAtom } from '../../../services/state/AppConfigStateJ';
import { useRoundDocuments } from '../../../services/queries-hooks/MaggieRoundDocumentQueriesHooks';
import { getErrorMessage } from '../../../services/queryHelpers';
import { colors } from '../../../theme/colors';
import { isZipFile } from '../../../util/file-utils';
import { MAX_FILE_SIZE_BYTES as MAX_ZIP_SIZE } from '../../DocumentHub/documentHub.constants';
import { convertFileSize, validateFileExtension } from '../../KPI/utils';
import { CollapsibleSectionCard } from './CollapsibleSectionCard';
import { collapsedCardHeight } from './deals-detail.styles';
import { RoundDocumentsGrid } from './RoundDocumentsGrid';
import { useRoundPendingDocuments, useUploadRoundDocuments } from './useRoundDocHubUploads';

// Strip control characters; React escapes text in JSX, this is defensive validation.
// NOTE: duplicated from RoundCard.tsx; to be consolidated when FD-3363 refactors the sidebar.
const sanitize = (value: string, maxLength: number): string =>
  value
    .split('')
    .filter((char) => {
      const code = char.charCodeAt(0);
      return code >= 32 && code !== 127;
    })
    .join('')
    .substring(0, maxLength);

// A ZIP expands server-side into its files, each classified like a standalone upload.
const UPLOAD_EXTENSIONS = new Set([...ALLOWED_EXTENSIONS, 'zip']);
const FILE_INPUT_ACCEPT = Array.from(UPLOAD_EXTENSIONS)
  .map((ext) => `.${ext}`)
  .join(',');

const DOC_HUB_PATH = `/${ROUTES.DOCS}`;

// Solid outline on purpose: a dashed border reads as a drag-and-drop target.
const uploadButtonSx = {
  textTransform: 'none',
} as const;

const pendingCss = css`
  font-size: 12px;
  line-height: 1.66;
  color: ${colors.neutral[60]};
  white-space: nowrap;
`;

const uploadErrorCss = css`
  max-width: 260px;
  font-size: 12px;
  line-height: 1.66;
  color: ${colors.critical[50]};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// The shared header actions box only separates items by 2px; the Upload button needs real air.
const headerActionsCss = css`
  display: flex;
  align-items: center;
  gap: 10px;
`;

interface RoundDocumentsSectionProps {
  round: IFinanceRoundDataModel;
}

/**
 * Documents section of the Deals-tab detail pane. Shows an upload control (edit
 * permission) and the uploaded deal documents list. Uploads go through Doc Hub with the
 * round attached; a document reaches the list once it is classified and filed on the round.
 */
export const RoundDocumentsSection: FC<RoundDocumentsSectionProps> = ({ round }) => {
  const { data: documents, isLoading } = useRoundDocuments(round.id);
  const uploadMutation = useUploadRoundDocuments(round);
  const [extractedZipKeys, setExtractedZipKeys] = useState<string[]>([]);
  const pending = useRoundPendingDocuments(round.id, round.companyId, extractedZipKeys);
  const [uploadError, setUploadError] = useState<string | undefined>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canEdit = PermissionService.get().hasPermission(PermissionKey.canEditTransaction);
  // Round documents only reach extraction through Starbreaker, which consolidated
  // data collection gates.
  const usingConsolidatedDataCollection = Boolean(
    useAtomValue(clientConfigFieldsAtom)?.Using_Consolidated_Data_Collection
  );
  const canUpload = canEdit && usingConsolidatedDataCollection;

  const validateFiles = (files: File[]): string | null => {
    if (files.length > MAX_BATCH_UPLOAD_SIZE) {
      return `Cannot upload more than ${MAX_BATCH_UPLOAD_SIZE} files at once.`;
    }
    for (const file of files) {
      if (!validateFileExtension(file.name, UPLOAD_EXTENSIONS)) {
        return `Invalid file type for ${sanitize(file.name, 255)}. Allowed formats: ${Array.from(UPLOAD_EXTENSIONS).join(', ').toUpperCase()}`;
      }
      const maxSize = isZipFile(file) ? MAX_ZIP_SIZE : MAX_FILE_SIZE;
      if (file.size > maxSize) {
        return `File ${sanitize(file.name, 255)} exceeds ${maxSize / 1024 / 1024}MB limit. File size: ${convertFileSize(file.size)}`;
      }
    }
    return null;
  };

  const handleFilesChosen = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    // Reset so picking the same file again re-fires the change event.
    event.target.value = '';
    if (files.length === 0) {
      return;
    }
    setUploadError(undefined);

    const validationError = validateFiles(files);
    if (validationError) {
      setUploadError(validationError);
      return;
    }

    try {
      const { extractedZipKeys: zipKeys } = await uploadMutation.mutateAsync(files);
      if (zipKeys.length > 0) {
        setExtractedZipKeys((prev) => [...prev, ...zipKeys]);
      }
    } catch (error) {
      // The API's reason when it gave one; otherwise the batch's own message (e.g. which file
      // failed to reach S3).
      const fallback = error instanceof Error ? error.message : 'Upload failed';
      setUploadError(sanitize(getErrorMessage(error, fallback), 1000));
    }
  };

  // Skeleton before the card mounts so isEmpty is only evaluated on settled data —
  // a blank round must start collapsed, not open-then-animate-closed.
  if (isLoading) {
    return <Skeleton variant='rectangular' height={collapsedCardHeight} />;
  }

  // Uploader lives in the always-visible header (review ask): a doc-less round
  // starts collapsed, and editors must still be able to upload without expanding.
  const headerRight = (
    <span css={headerActionsCss}>
      {uploadError && (
        <Tooltip title={uploadError}>
          <span css={uploadErrorCss}>{uploadError}</span>
        </Tooltip>
      )}
      {pending.analyzing > 0 && <span css={pendingCss}>{pending.analyzing} analyzing</span>}
      {pending.needsReview > 0 && (
        <Link component={RouterLink} to={DOC_HUB_PATH} color='secondary' underline='hover' variant='body2'>
          {pending.needsReview} need review in Doc Hub
        </Link>
      )}
      {canUpload && (
        <>
          <input
            ref={fileInputRef}
            type='file'
            hidden
            multiple
            accept={FILE_INPUT_ACCEPT}
            onChange={handleFilesChosen}
          />
          <Button
            size='small'
            variant='outlined'
            sx={uploadButtonSx}
            disabled={uploadMutation.isPending}
            startIcon={
              uploadMutation.isPending ? <CircularProgress size={14} /> : <PublishIcon fontSize='small' />
            }
            onClick={() => fileInputRef.current?.click()}
          >
            Upload
          </Button>
        </>
      )}
    </span>
  );

  return (
    <RoundDocumentsSectionView
      roundId={round.id}
      documents={documents ?? []}
      canEdit={canEdit}
      headerRight={headerRight}
    />
  );
};

export interface RoundDocumentsSectionViewProps {
  roundId: number;
  documents: IRoundDocumentMetadata[];
  canEdit: boolean;
  headerRight?: ReactNode;
}

/** Rendering half of the section: the container supplies the upload control, a preview supplies sample documents. */
export const RoundDocumentsSectionView: FC<RoundDocumentsSectionViewProps> = ({
  roundId,
  documents,
  canEdit,
  headerRight,
}) => (
  <CollapsibleSectionCard
    title='Documents'
    isEmpty={documents.length === 0}
    emptyAnnotation='No documents'
    headerRight={headerRight}
  >
    <RoundDocumentsGrid roundId={roundId} documents={documents} canEdit={canEdit} />
  </CollapsibleSectionCard>
);
